import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { utcToZonedTime } from 'date-fns-tz'

import { ApplicationState } from '@/state/root.models'
import useForm from '@/hooks/form/use-form.hook'
import {
  FormStatusType,
  BonusPeriodRecurrenceEnum,
  TimeZoneUTCType,
} from '@/enums'

import { getTimeFromDate, setTimeForToday } from '@/utils/date'
import { DEFAULT_BONUS_PRIORITY } from '@/constants'
import { bonusDetailsSchema } from '../components/schema'
import bonusDetailsValidator from '../components/validator'

const useBonusDetails = () => {
  const { currentBonus, bonusFormMode } = useSelector(
    ({ bonusEngine }: ApplicationState) => bonusEngine,
  )

  const { state, handleOnChange, disable, setFormValues, setDisable } = useForm(
    bonusDetailsSchema,
    bonusDetailsValidator,
    () => {},
  )

  const setCurrentBonusDetails = () => {
    let startDate = ''
    let startTime = ''
    let endDate = ''
    let endTime = ''

    if (
      currentBonus.bonusPeriod?.recurrence !==
        BonusPeriodRecurrenceEnum.daily &&
      currentBonus.bonusPeriod?.recurrence !== BonusPeriodRecurrenceEnum.weekday
    ) {
      if (currentBonus.bonusPeriod?.startDate) {
        const localDateTimeStartDate = utcToZonedTime(
          new Date(currentBonus.bonusPeriod?.startDate),
          TimeZoneUTCType['GMT+00 Coordinated Universal Time'],
        )

        startDate = localDateTimeStartDate.toLocaleDateString()
        startTime = getTimeFromDate(localDateTimeStartDate)
      }

      if (currentBonus.bonusPeriod?.endDate) {
        const localDateTimeEndDate = utcToZonedTime(
          new Date(currentBonus.bonusPeriod?.endDate),
          TimeZoneUTCType['GMT+00 Coordinated Universal Time'],
        )

        endDate = localDateTimeEndDate.toLocaleDateString()
        endTime = getTimeFromDate(localDateTimeEndDate)
      }
    } else {
      if (currentBonus.bonusPeriod?.startDate) {
        startTime = currentBonus.bonusPeriod?.startDate
      }
      if (currentBonus.bonusPeriod?.endDate) {
        endTime = currentBonus.bonusPeriod?.endDate
      }
    }

    setFormValues({
      name: {
        value: currentBonus.name || '',
        error: '',
      },
      description: {
        value: currentBonus.description || '',
        error: '',
      },
      code: {
        value: currentBonus.code || '',
        error: '',
      },
      type: {
        value: currentBonus.type || '',
        error: '',
      },
      priority: {
        // bonuses created before the priority field exists fall back to the default
        value: currentBonus.priority ?? DEFAULT_BONUS_PRIORITY,
        error: '',
      },
      recurrence: {
        value: currentBonus.bonusPeriod?.recurrence,
        error: '',
      },
      startDate: {
        value: startDate || null,
        error: '',
      },
      startTime: {
        value: startTime || '00:00:00',
        error: '',
      },
      endDate: {
        value: endDate || null,
        error: '',
      },
      endTime: {
        value: endTime || '00:00:00',
        error: '',
      },
      recurrenceWeekday: {
        value: currentBonus.bonusPeriod?.weekday?.recurrence,
        error: '',
      },
      day: {
        value: currentBonus.bonusPeriod?.weekday?.day,
        error: '',
      },
    })
  }

  useEffect(() => {
    if (
      bonusFormMode === FormStatusType.EDIT ||
      bonusFormMode === FormStatusType.DUPLICATE
    ) {
      setDisable(false)
    }
  }, [currentBonus]) // eslint-disable-line

  useEffect(() => {
    if (state.recurrence.value && state.recurrence.value !== '') {
      if (state.recurrence.value === BonusPeriodRecurrenceEnum.daily) {
        const startDate = new Date()
        const endDate = new Date()

        const timeStartDate = setTimeForToday(state.startTime.value)
        if (timeStartDate.toString() !== 'Invalid Date') {
          startDate.setHours(
            Number(timeStartDate.getHours()),
            Number(timeStartDate.getMinutes()),
            0,
            0,
          )
        }
        const timeEndDate = setTimeForToday(state.endTime.value)
        if (timeEndDate.toString() !== 'Invalid Date') {
          endDate.setHours(
            Number(timeEndDate.getHours()),
            Number(timeEndDate.getMinutes()),
            0,
            0,
          )
        }

        if (startDate > endDate) {
          setDisable(true)
        }
      } else {
        const startDate = new Date(state.startDate.value)
        const endDate = new Date(state.endDate.value)

        const timeStartDate = setTimeForToday(state.startTime.value)
        if (timeStartDate.toString() !== 'Invalid Date') {
          startDate.setHours(
            Number(timeStartDate.getHours()),
            Number(timeStartDate.getMinutes()),
            0,
            0,
          )
        }
        const timeEndDate = setTimeForToday(state.endTime.value)
        if (timeEndDate.toString() !== 'Invalid Date') {
          endDate.setHours(
            Number(timeEndDate.getHours()),
            Number(timeEndDate.getMinutes()),
            0,
            0,
          )
        }

        if (startDate > endDate) {
          setDisable(true)
        }
      }
    }
  }, [state]) // eslint-disable-line

  return {
    bonusDetails: {
      state,
      handleOnChange,
      disable,
      setFormValues,
      setDisable,
    },
    setCurrentBonusDetails,
  }
}

export default useBonusDetails
